import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  KubernetesSetupFormValues,
  KubernetesSetupPolicyPayload,
  KUBERNETES_SETUP_FORM_INITIAL_VALUES,
  KUBERNETES_SETUP_INITIAL_VALUES,
  DEFAULT_ACCESS_MODE,
} from './types';

/**
 * Type guard to check if data is KubernetesSetupPolicyPayload
 */
function isKubernetesSetupPolicyPayload(
  data: unknown
): data is KubernetesSetupPolicyPayload {
  return typeof data === 'object' && data !== null && 'UseLoadBalancer' in data;
}

/**
 * Hook to get initial values for Kubernetes Setup Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Complete form initial values
 */
export function useKubernetesSetupInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesSetupFormValues {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_SETUP_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesSetupPolicyPayload(data)) {
      return {
        // Common fields from policy metadata
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        // Type-specific defaults (using type-specific only, not full form values)
        ...KUBERNETES_SETUP_INITIAL_VALUES,
      };
    }

    return {
      // Common fields from policy metadata
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      // Type-specific fields from policy data
      useLoadBalancer: data.UseLoadBalancer?.value ?? false,
      // Map from PascalCase API payload to camelCase form data
      ingressControllers: Array.isArray(data.IngressClasses)
        ? data.IngressClasses.map((ic) => ({ name: ic.value?.Name ?? '' }))
        : [],
      allowNoneIngressClass: data.AllowNoneIngressClass?.value ?? false,
      ingressAvailabilityPerNamespace:
        data.IngressAvailabilityPerNamespace?.value ?? false,
      restrictStandardUserIngressW:
        data.RestrictStandardUserIngressW?.value ?? false,
      changeWindow: data.EndpointChangeWindow?.value ?? {
        Enabled: false,
        StartTime: '22:00',
        EndTime: '02:00',
      },
      enableResourceOverCommit: data.EnableResourceOverCommit?.value ?? false,
      resourceOverCommitPercentage:
        data.ResourceOverCommitPercentage?.value ?? 0,
      useServerMetrics: data.UseServerMetrics?.value ?? false,
      // Map from PascalCase API payload to camelCase form data
      storageClasses: Array.isArray(data.StorageClasses)
        ? data.StorageClasses.map((sc) => ({
            name: sc.value?.Name ?? '',
            accessModes: sc.value?.AccessModes ?? [DEFAULT_ACCESS_MODE],
            allowVolumeExpansion: sc.value?.AllowVolumeExpansion ?? false,
          }))
        : [],
    };
  }, [policy]);
}
